import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { subscribeToBillingGate } from '@/api/billingGate';
import { env } from '@/config/env';
import { useAuth } from '@/hooks/useAuth';
import { useOnboarding } from './useOnboarding';

export type BillingGate = {
  /** Billing is enforced for this org (frontend flag + backend `billing_enabled`). */
  billingEnabled: boolean;
  /** The org has no active/trial subscription → writes are gated to read-only. */
  isReadOnly: boolean;
  /** Whether the current user may manage billing (subscribe / open the portal). */
  isBillingAdmin: boolean;
};

/**
 * Read-only billing gate state, derived purely from onboarding —
 * `steps.subscription_active === false` is the single source of truth for the
 * paywall. Safe to call from any component; holds no state and triggers no
 * side effects (the reactive refresh lives in {@link useBillingGateSync}).
 */
export const useBillingGate = (): BillingGate => {
  const { user } = useAuth();
  const { data } = useOnboarding();

  const billingEnabled = env.billingEnabled && Boolean(data?.billing_enabled);
  const isReadOnly = billingEnabled && data?.steps.subscription_active === false;
  const isBillingAdmin = data
    ? data.is_billing_admin
    : user?.role === 'ORGANIZATION_ADMIN' || user?.role === 'PLATFORM_ADMIN';

  return { billingEnabled, isReadOnly, isBillingAdmin };
};

/**
 * Mounts the reactive half of the gate: when a write hits the backend's 403
 * `subscription_inactive` mid-session, refetch onboarding so the proactive
 * read-only state (and the paywall banner) catch up. Mount ONCE in the app shell
 * — multiple mounts would each invalidate on the same event.
 */
export const useBillingGateSync = (): void => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!env.billingEnabled) {
      return;
    }

    return subscribeToBillingGate(() => {
      void queryClient.invalidateQueries({ queryKey: ['billing-onboarding'] });
    });
  }, [queryClient]);
};
